import { useTranslation } from "react-i18next";
import colors from "tailwindcss/colors";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { useMemo } from "react";
import { GetExpeditionStatsResponseDto } from "@/types/api/api-types";
import moment from "moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  {
    id: "textTop",
    afterDatasetsDraw: function (chart: any, _) {
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height - 40;
      const fontSize = chart.options?.plugins?.textTop?.fontSize || "16";
      ctx.font = fontSize + "px Arial";
      ctx.fillStyle = chart.options?.plugins?.textTop?.color || "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const text = chart.options?.plugins?.textTop?.text || "black";
      const textX = Math.round(width / 2);
      const textY = Math.round(height / 2 - fontSize / 2);
      ctx.fillText(text, textX, textY);
    },
  },
  {
    id: "textUnder",
    afterDatasetsDraw: function (chart: any, _) {
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height - 40;
      const fontSize = chart.options?.plugins?.textUnder?.fontSize || "16";
      ctx.font = fontSize + "px Arial";
      ctx.fillStyle = chart.options?.plugins?.textUnder?.color || "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const text = chart.options?.plugins?.textUnder?.text || "black";
      const textX = Math.round(width / 2);
      const textY = Math.round(height / 2 + fontSize / 2);
      ctx.fillText(text, textX, textY);
    },
  },
);

type CaProps = {
  expeditionStats?: GetExpeditionStatsResponseDto["data"];
  timeScale: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
};

const generateDatesBetween = (startDate: moment.Moment, endDate: moment.Moment): string[] => {
  const dates = [];
  const currentDate = startDate.clone();

  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate.format("YYYY-MM-DD")); // Format each date as "YYYY-MM-DD"
    currentDate.add(1, "day"); // Move to the next day
  }

  return dates;
};

export const Ca = ({ expeditionStats, timeScale, startDate, endDate }: CaProps) => {
  const { t } = useTranslation("boClients");
  const { width } = useWindowDimensions();

  const computeOtherFees = () => {
    if (timeScale === "week" || timeScale === "month") {
      const dateRange = generateDatesBetween(startDate, endDate).reduce(
        (acc: { [date: string]: number }, date) => {
          acc[date] = 0;
          return acc;
        },
        {},
      );
      const feesByDay = expeditionStats?.reduce((acc: { [date: string]: number }, item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const day = moment(refDate).format("YYYY-MM-DD");
        acc[day] = (acc[day] || 0) + (item.quotation ? item.quotation.otherFees : 0);
        return acc;
      }, dateRange);
      const sortedFeesArray = feesByDay
        ? Object.entries(feesByDay)
            .map(([date, totalFees]) => ({ date, totalFees }))
            .sort((a, b) => moment(a.date).diff(moment(b.date)))
        : [];
      return sortedFeesArray.map((item) => item.totalFees.toFixed(0));
    } else {
      const monthDataAggregate: number[] = Array(12).fill(0);
      expeditionStats?.forEach((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const month = moment(refDate).month();
        monthDataAggregate[month] += (item.quotation ? item.quotation.otherFees : 0) || 0;
      });
      return monthDataAggregate.map((item) => item.toFixed(0));
    }
  };

  const computeSales = () => {
    if (timeScale === "week" || timeScale === "month") {
      const dateRange = generateDatesBetween(startDate, endDate).reduce(
        (acc: { [date: string]: number }, date) => {
          acc[date] = 0;
          return acc;
        },
        {},
      );
      expeditionStats?.map((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const day = moment(refDate).format("YYYY-MM-DD");
        dateRange[day] =
          (dateRange[day] || 0) +
          (item.quotation?.deliveryContent
            ?.map((content) => content.unitPrice * content.numberOfBottles)
            .reduce((a, b) => a + b, 0) || 0);
      }, dateRange);
      expeditionStats?.forEach((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const day = moment(refDate).format("YYYY-MM-DD");
        let customCotationValue = 0;
        if (item.customQuotation?.type === "PALETTE") {
          customCotationValue =
            item.customQuotation?.paletteDetail?.palettePackages?.reduce(
              (acc, el) => acc + el.value,
              0,
            ) || 0;
        } else if (item.customQuotation?.type === "PARCEL") {
          customCotationValue =
            item.customQuotation?.parcelDetail?.parcelPackages?.reduce(
              (acc, el) => acc + el.value,
              0,
            ) || 0;
        } else if (item.customQuotation?.type === "OTHER") {
          customCotationValue = item.customQuotation?.otherDetail?.value || 0;
        }
        dateRange[day] = (dateRange[day] || 0) + customCotationValue;
      });
      const sortedFeesArray = dateRange
        ? Object.entries(dateRange)
            .map(([date, totalFees]) => ({ date, totalFees }))
            .sort((a, b) => moment(a.date).diff(moment(b.date)))
        : [];
      return sortedFeesArray.map((item) => item.totalFees.toFixed(0));
    } else {
      const monthDataAggregate: number[] = Array(12).fill(0);
      expeditionStats?.forEach((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const month = moment(refDate).month();
        monthDataAggregate[month] +=
          item.quotation?.deliveryContent
            ?.map((content) => content.unitPrice * content.numberOfBottles)
            .reduce((a, b) => a + b, 0) || 0;
      });
      expeditionStats?.forEach((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const month = moment(refDate).month();
        let customCotationValue = 0;
        if (item.customQuotation?.type === "PALETTE") {
          customCotationValue =
            item.customQuotation?.paletteDetail?.palettePackages?.reduce(
              (acc, el) => acc + el.value,
              0,
            ) || 0;
        } else if (item.customQuotation?.type === "PARCEL") {
          customCotationValue =
            item.customQuotation?.parcelDetail?.parcelPackages?.reduce(
              (acc, el) => acc + el.value,
              0,
            ) || 0;
        } else if (item.customQuotation?.type === "OTHER") {
          customCotationValue = item.customQuotation?.otherDetail?.value || 0;
        }
        monthDataAggregate[month] = (monthDataAggregate[month] || 0) + customCotationValue;
      });
      return monthDataAggregate.map((item) => item.toFixed(0));
    }
  };

  const computePelicanFees = () => {
    const pelicanFeesTotal = (data: GetExpeditionStatsResponseDto["data"][0]) => {
      return {
        value:
          data.expeditionBillingDetail.expeditionInvoiceData.insurance / 100 +
          data.expeditionBillingDetail.expeditionInvoiceData.packaging / 100 +
          data.expeditionBillingDetail.expeditionInvoiceData.packagingProtection / 100 +
          data.expeditionBillingDetail.expeditionInvoiceData.transportHt / 100 +
          data.expeditionBillingDetail.expeditionInvoiceData.taxes / 100 +
          data.expeditionBillingDetail.expeditionInvoiceData.tva / 100,
        details: {
          insurance: data.expeditionBillingDetail.expeditionInvoiceData.insurance / 100,
          packaging: data.expeditionBillingDetail.expeditionInvoiceData.packaging / 100,
          packagingProtection:
            data.expeditionBillingDetail.expeditionInvoiceData.packagingProtection / 100,
          transportHt: data.expeditionBillingDetail.expeditionInvoiceData.transportHt / 100,
          taxes: data.expeditionBillingDetail.expeditionInvoiceData.taxes / 100,
          tva: data.expeditionBillingDetail.expeditionInvoiceData.tva / 100,
        },
      };
    };

    if (timeScale === "week" || timeScale === "month") {
      const dateRange = generateDatesBetween(startDate, endDate).reduce(
        (
          acc: {
            [date: string]: {
              value: number;
              details: {
                insurance: number;
                packaging: number;
                packagingProtection: number;
                transportHt: number;
                taxes: number;
                tva: number;
              };
            };
          },
          date,
        ) => {
          acc[date] = {
            value: 0,
            details: {
              insurance: 0,
              packaging: 0,
              packagingProtection: 0,
              transportHt: 0,
              taxes: 0,
              tva: 0,
            },
          };
          return acc;
        },
        {},
      );
      expeditionStats?.map((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const day = moment(refDate).format("YYYY-MM-DD");
        const pelicanFees = pelicanFeesTotal(item);
        dateRange[day] = {
          value: (dateRange[day].value || 0) + pelicanFees.value,
          details: {
            insurance: pelicanFees.details.insurance + dateRange[day].details.insurance,
            packaging: pelicanFees.details.packaging + dateRange[day].details.packaging,
            packagingProtection:
              pelicanFees.details.packagingProtection + dateRange[day].details.packagingProtection,
            transportHt: pelicanFees.details.transportHt + dateRange[day].details.transportHt,
            taxes: pelicanFees.details.taxes + dateRange[day].details.taxes,
            tva: pelicanFees.details.tva + dateRange[day].details.tva,
          },
        };
      }, dateRange);
      const sortedFeesArray = dateRange
        ? Object.entries(dateRange)
            .map(([date, totalFees]) => ({ date, totalFees }))
            .sort((a, b) => moment(a.date).diff(moment(b.date)))
        : [];
      // return sortedFeesArray.map((item) => item.totalFees.value.toFixed(0));
      return sortedFeesArray.map((item) => item.totalFees);
    } else {
      // const monthDataAggregate: {value: number, details: { insurt }} = Array(12).fill(0);
      const monthDataAggregate: {
        value: number;
        details: {
          insurance: number;
          packaging: number;
          packagingProtection: number;
          transportHt: number;
          taxes: number;
          tva: number;
        };
      }[] = Array(12).fill({
        value: 0,
        details: {
          insurance: 0,
          packaging: 0,
          packagingProtection: 0,
          transportHt: 0,
          taxes: 0,
          tva: 0,
        },
      });
      expeditionStats?.forEach((item) => {
        const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
        const month = moment(refDate).month();
        if (isNaN(month)) {
          return;
        }
        const pelicanFees = pelicanFeesTotal(item);
        monthDataAggregate[month] = {
          value: monthDataAggregate[month].value + pelicanFees.value,
          details: {
            insurance: pelicanFees.details.insurance + monthDataAggregate[month].details.insurance,
            packaging: pelicanFees.details.packaging + monthDataAggregate[month].details.packaging,
            packagingProtection:
              pelicanFees.details.packagingProtection +
              monthDataAggregate[month].details.packagingProtection,
            transportHt:
              pelicanFees.details.transportHt + monthDataAggregate[month].details.transportHt,
            taxes: pelicanFees.details.taxes + monthDataAggregate[month].details.taxes,
            tva: pelicanFees.details.tva + monthDataAggregate[month].details.tva,
          },
        };
      });
      return monthDataAggregate;
    }
  };

  const otherFees = useMemo(
    () =>
      computeOtherFees().map((item) => ({
        value: Number(item),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expeditionStats, computeOtherFees],
  );
  const sales = useMemo(
    () =>
      computeSales().map((item) => ({
        value: Number(item),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expeditionStats, computeSales],
  );
  const pelicanFees = useMemo(
    () => computePelicanFees(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expeditionStats, computePelicanFees],
  );

  const data = useMemo(() => {
    const labels =
      timeScale === "year"
        ? "janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre".split(
            "_",
          )
        : generateDatesBetween(startDate, endDate);
    return {
      datasets: [
        {
          label: t("profileStatisticsCa.sales"),
          data: sales.map((item, index) => ({
            id: labels[index],
            nested: item,
          })),
          backgroundColor: colors.rose[900],
          borderRadius: 8,
          ...(width < 768 && { categoryPercentage: 0.95, barPercentage: 0.95 }),
        },

        {
          label: t("profileStatisticsCa.pelicanFees"),
          data: pelicanFees.map((item, index) => ({
            id: labels[index],
            nested: item,
          })),
          backgroundColor: colors.blue[500],
          borderRadius: 8,
          ...(width < 768 && { categoryPercentage: 0.95, barPercentage: 0.95 }),
        },
        {
          label: t("profileStatisticsCa.otherFees"),
          data: otherFees.map((item, index) => ({
            id: labels[index],
            nested: item,
          })),
          backgroundColor: colors.teal[500],
          borderRadius: 8,
          ...(width < 768 && { categoryPercentage: 0.95, barPercentage: 0.95 }),
        },
      ],
    };
  }, [t, width, sales, pelicanFees, otherFees, timeScale, startDate, endDate]);

  const barChartOptions = useMemo(() => {
    const isMobile = width < 768;
    return {
      parsing: {
        xAxisKey: "id",
        yAxisKey: "nested.value",
      },
      plugins: {
        legend: {
          display: false,
        },
        textTop: false,
        textUnder: false,
      },
      ...(isMobile && { responsive: true }),
      ...(!isMobile && { maintainAspectRatio: false }),
      scales: {
        x: {
          title: {
            display: !isMobile,
            text:
              timeScale === "year"
                ? t("profileStatisticsCa.monthAxis")
                : t("profileStatisticsCa.dayAxis"),
            fontSize: 16,
            padding: 10,
          },
          stacked: true,
        },
        y: {
          title: {
            display: !isMobile,
            text: "€",
            fontSize: 16,
            padding: 10,
          },
          stacked: true,
        },
      },
    };
  }, [t, width, timeScale]);

  const dataPie = {
    labels: [
      t("profileStatisticsCa.otherFees"),
      t("profileStatisticsCa.sales"),
      t("profileStatisticsCa.pelicanFees"),
    ],
    datasets: [
      {
        label: t("profileStatisticsCa.turnoverDataset"),
        data: [
          otherFees.reduce((a, b) => a + b.value, 0),
          sales.reduce((a, b) => a + b.value, 0),
          pelicanFees.reduce((a, b) => a + b.value, 0),
        ],
        backgroundColor: [colors.teal[500], colors.rose[900], colors.blue[500]],
        borderWidth: 0,
      },
    ],
  };

  const optionsPie: any = {
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `${Number(context.parsed).toFixed(2)} €`;
          },
        },
      },
      textTop: {
        text: `${(
          (otherFees.reduce((a, b) => a + b.value, 0) +
            sales.reduce((a, b) => a + b.value, 0) +
            pelicanFees.reduce((a, b) => a + b.value, 0)) /
          1000
        ).toFixed(1)}k €`,
        color: "#0C111D",
        fontSize: 20,
      },
      textUnder: {
        text: t("profileStatisticsCa.totalTurnover"),
        color: "#475569",
        fontSize: 20,
      },
    },
    responsive: true,
    cutout: "80%",
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-1 flex-col gap-5 divide-y rounded-xl border bg-white p-6 shadow-xs">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-xl font-semibold text-black">{t("profileStatisticsCa.title")}</h2>
        </div>
        <div className="flex w-full flex-col items-center justify-between gap-8 pt-5 lg:max-h-[400px] lg:flex-row lg:items-stretch">
          <div className="w-full flex-1">
            <Bar options={barChartOptions} data={data} />
          </div>
          <div className="w-fit">
            <Doughnut data={dataPie} options={optionsPie} />
          </div>
        </div>
      </div>
    </div>
  );
};
